// Module: vault-container
// Contract: ./contract.md   Spec: docs/vault-spec-v2.md §4
//
// Converts between vault plaintext and the encrypted on-the-wire container.

export type SizeClass = 1 | 2 | 3 | 4

export type Header = {
  version: number
  revision: bigint
  sizeClass: SizeClass
}

export type RandomSource = (out: Uint8Array) => void

// TypeScript 5.7 made Uint8Array generic over its backing buffer, so a `subarray()` result is
// Uint8Array<ArrayBufferLike> and no longer satisfies BufferSource. The bytes are identical; only
// the type needs narrowing.
const src = (u: Uint8Array): BufferSource => u as unknown as BufferSource

export type ContainerErrorCode =
  | "BadMagic"
  | "UnsupportedVersion"
  | "TruncatedContainer"
  | "AuthenticationFailed"
  | "LengthPrefixInvalid"
  | "PayloadTooLarge"

export class ContainerError extends Error {
  readonly code: ContainerErrorCode
  constructor(code: ContainerErrorCode, message?: string) {
    super(message ?? code)
    this.name = "ContainerError"
    this.code = code
  }
}

export const MAGIC = Uint8Array.from([0x56, 0x41, 0x55, 0x4c]) // "VAUL"
export const VERSION = 2

const HEADER_BYTES = 28
const NONCE_OFFSET = 16
const NONCE_BYTES = 12
const TAG_BYTES = 16
const LENGTH_PREFIX_BYTES = 4

const SIZE_CLASS_BYTES: Record<SizeClass, number> = {
  1: 256 * 1024,
  2: 1024 * 1024,
  3: 4 * 1024 * 1024,
  4: 16 * 1024 * 1024,
}

const defaultRng: RandomSource = (out) => {
  crypto.getRandomValues(out)
}

// Web Crypto rejects a single getRandomValues call above 65,536 bytes, and the padding region
// runs to 16 MB. Chunking here rather than in defaultRng means a RandomSource — including an
// injected one — is never asked for more than the platform allows.
const MAX_RANDOM_BYTES = 65536

function fillRandom(rng: RandomSource, out: Uint8Array): void {
  for (let offset = 0; offset < out.length; offset += MAX_RANDOM_BYTES) {
    rng(out.subarray(offset, Math.min(offset + MAX_RANDOM_BYTES, out.length)))
  }
}

function isSizeClass(value: number): value is SizeClass {
  return value === 1 || value === 2 || value === 3 || value === 4
}

/** Smallest class that fits `needed`. Exactly filling a class uses that class, not the next up. */
function chooseSizeClass(needed: number): SizeClass {
  for (const id of [1, 2, 3, 4] as const) {
    if (needed <= SIZE_CLASS_BYTES[id]) return id
  }
  throw new ContainerError("PayloadTooLarge", `payload of ${needed} bytes exceeds the largest size class`)
}

function buildHeader(revision: bigint, sizeClass: SizeClass, nonce: Uint8Array): Uint8Array {
  const header = new Uint8Array(HEADER_BYTES)
  header.set(MAGIC, 0)
  const view = new DataView(header.buffer)
  view.setUint16(4, VERSION, false)
  view.setBigUint64(6, revision, false)
  header[14] = sizeClass
  header[15] = 0 // reserved
  header.set(nonce, NONCE_OFFSET)
  return header
}

/**
 * Parses the header without a key, so a rollback check (spec §6.3) need not decrypt up to 16 MB.
 *
 * The returned values are UNAUTHENTICATED. They may be used only to reject a container — a
 * revision below the stored checkpoint — never to accept or trust one. Anything acted on
 * positively must come from `unpack`, whose header is covered by the GCM tag.
 */
export function readHeader(container: Uint8Array): Header {
  if (container.length < HEADER_BYTES + TAG_BYTES) {
    throw new ContainerError("TruncatedContainer", `container is ${container.length} bytes`)
  }
  for (let i = 0; i < MAGIC.length; i += 1) {
    if (container[i] !== MAGIC[i]) throw new ContainerError("BadMagic")
  }

  const view = new DataView(container.buffer, container.byteOffset, container.byteLength)
  const version = view.getUint16(4, false)
  if (version !== VERSION) {
    throw new ContainerError("UnsupportedVersion", `container version ${version}, expected ${VERSION}`)
  }

  const sizeClassId = container[14]!
  if (!isSizeClass(sizeClassId)) {
    throw new ContainerError("UnsupportedVersion", `unknown size class ${sizeClassId}`)
  }

  return { version, revision: view.getBigUint64(6, false), sizeClass: sizeClassId }
}

export async function pack(opts: {
  plaintext: Uint8Array
  key: CryptoKey
  revision: bigint
  rng?: RandomSource
}): Promise<Uint8Array> {
  const { plaintext, key, revision } = opts
  const rng = opts.rng ?? defaultRng

  const needed = LENGTH_PREFIX_BYTES + plaintext.length
  const sizeClass = chooseSizeClass(needed)
  const payloadBytes = SIZE_CLASS_BYTES[sizeClass]

  // [uint32 BE length][plaintext][CSPRNG padding to the size class]
  const payload = new Uint8Array(payloadBytes)
  new DataView(payload.buffer).setUint32(0, plaintext.length, false)
  payload.set(plaintext, LENGTH_PREFIX_BYTES)
  const padding = payload.subarray(needed)
  if (padding.length > 0) fillRandom(rng, padding)

  const nonce = new Uint8Array(NONCE_BYTES)
  rng(nonce)
  const header = buildHeader(revision, sizeClass, nonce)

  const ciphertext = new Uint8Array(
    await crypto.subtle.encrypt(
      { name: "AES-GCM", iv: src(nonce), additionalData: src(header), tagLength: TAG_BYTES * 8 },
      key,
      src(payload),
    ),
  )

  const container = new Uint8Array(HEADER_BYTES + ciphertext.length)
  container.set(header, 0)
  container.set(ciphertext, HEADER_BYTES)
  return container
}

export async function unpack(opts: {
  container: Uint8Array
  key: CryptoKey
}): Promise<{ plaintext: Uint8Array; header: Header }> {
  const { container, key } = opts

  // Structural validation first: never attempt to decrypt something that is not a container.
  const header = readHeader(container)

  const nonce = container.subarray(NONCE_OFFSET, NONCE_OFFSET + NONCE_BYTES)
  const aad = container.subarray(0, HEADER_BYTES)
  const ciphertext = container.subarray(HEADER_BYTES)

  let payload: Uint8Array
  try {
    payload = new Uint8Array(
      await crypto.subtle.decrypt(
        { name: "AES-GCM", iv: src(nonce), additionalData: src(aad), tagLength: TAG_BYTES * 8 },
        key,
        src(ciphertext),
      ),
    )
  } catch {
    // A wrong key and a tampered container must be indistinguishable: same code, no detail.
    throw new ContainerError("AuthenticationFailed")
  }

  if (payload.length < LENGTH_PREFIX_BYTES) throw new ContainerError("LengthPrefixInvalid")
  const declared = new DataView(payload.buffer, payload.byteOffset, payload.byteLength).getUint32(0, false)
  if (declared > payload.length - LENGTH_PREFIX_BYTES) {
    // Defence in depth: GCM already authenticates, but a future buggy writer must not be able to
    // make a reader over-read.
    throw new ContainerError("LengthPrefixInvalid", `declared length ${declared} exceeds payload`)
  }

  return {
    plaintext: payload.slice(LENGTH_PREFIX_BYTES, LENGTH_PREFIX_BYTES + declared),
    header,
  }
}

export const __testing = {
  SIZE_CLASS_BYTES,
  HEADER_BYTES,
  TAG_BYTES,
  LENGTH_PREFIX_BYTES,
  MAX_RANDOM_BYTES,
}
